/**
 * Shared CSS constants for the Rovo animated gradient border.
 *
 * The conic gradient rotates via a CSS custom property (`--gradient-angle`)
 * animated with `@keyframes`. Because `@property` at-rules and `@keyframes`
 * cannot be expressed in `cssMap` / `xcss`, they are injected via `<style>`
 * tags by the components below.
 *
 * The gradient background itself is also set as a CSS custom property
 * (`--rovo-gradient-background`) so consumers can reference it without
 * importing a style value (which would violate no-imported-style-values).
 *
 * Usage in cssMap / style prop:
 *   background: 'var(--rovo-gradient-background)',
 *   animation: 'rovoGradientRotation linear 2s infinite',
 */

import React from 'react';

const ROVO_GRADIENT_CSS = `
:root {
	--rovo-gradient-background: conic-gradient(
		from var(--gradient-angle, 45deg),
		#FCA700 10%,
		#6A9A23 30%,
		#6A9A23 50%,
		#1868DB 70%,
		#AF59E0 90%
	);
}

@property --gradient-angle {
	syntax: "<angle>";
	initial-value: 270deg;
	inherits: false;
}
`;

const ROVO_GRADIENT_KEYFRAMES_CSS = `${ROVO_GRADIENT_CSS}
@keyframes rovoGradientRotation {
	0% { --gradient-angle: 0deg; }
	100% { --gradient-angle: 360deg; }
}
`;

/**
 * Injects a `<style>` tag declaring the gradient custom property and only
 * the `@property` rule for `--gradient-angle`. Use when keyframes are handled
 * separately (e.g. via `cssUnbounded`).
 */
export const RovoGradientPropertyStyle = (): React.ReactElement =>
	// eslint-disable-next-line react/no-danger, @atlaskit/ui-styling-standard/no-global-styles -- @property for CSS custom property animation
	React.createElement('style', { dangerouslySetInnerHTML: { __html: ROVO_GRADIENT_CSS } });

/**
 * Injects a `<style>` tag declaring the gradient custom property, the
 * `@property` rule, and `@keyframes rovoGradientRotation`.
 *
 * Reference the animation in `cssMap` as:
 * ```ts
 * animation: 'rovoGradientRotation linear 2s infinite',
 * ```
 * Reference the gradient background as:
 * ```ts
 * background: 'var(--rovo-gradient-background)',
 * ```
 */
export const RovoGradientKeyframesStyle = (): React.ReactElement =>
	// eslint-disable-next-line react/no-danger, @atlaskit/ui-styling-standard/no-global-styles -- @property and @keyframes for CSS custom property animation
	React.createElement('style', { dangerouslySetInnerHTML: { __html: ROVO_GRADIENT_KEYFRAMES_CSS } });
